/**
 * Validation message tokens.
 *
 * zod produces every validation message as a token: an i18n key, optionally
 * followed by `|` and a JSON object of interpolation values.
 *
 *   validation:required
 *   validation:tooSmall.string|{"minimum":3}
 *   auth:errors.passwordsDoNotMatch
 *
 * The token travels through zodResolver, react-hook-form and server action
 * responses as a plain string, and is translated where it is shown
 * (FormMessage, the toast wrapper). A string that is not a token is shown as
 * it is, so an untranslated message never disappears.
 */

export type ValidationMessageValues = Record<string, string | number>;

export interface ValidationMessage {
  key: string;
  values?: ValidationMessageValues;
}

// namespace:dotted.key, optionally |{json}
const TOKEN = /^([a-zA-Z][\w-]*:[\w.]+)(?:\|(\{[\s\S]*\}))?$/;

export function formatValidationMessage(
  key: string,
  values?: ValidationMessageValues,
): string {
  if (!values || Object.keys(values).length === 0) {
    return key;
  }

  return `${key}|${JSON.stringify(values)}`;
}

export function parseValidationMessage(
  message: string,
): ValidationMessage | null {
  const match = TOKEN.exec(message);

  if (!match) {
    return null;
  }

  const [, key, rawValues] = match;

  if (!rawValues) {
    return { key: key as string };
  }

  try {
    const values: unknown = JSON.parse(rawValues);

    if (values && typeof values === 'object' && !Array.isArray(values)) {
      return { key: key as string, values: values as ValidationMessageValues };
    }
  } catch {
    // A malformed value block is shown as plain text below.
  }

  return null;
}

/**
 * Resolves a message with a caller-supplied lookup, for consumers that are not
 * React (MCP tool errors, API JSON, logs). The lookup returns undefined for an
 * unknown key, and the message is then returned unchanged.
 */
export function resolveValidationMessage(
  message: string,
  lookup: (key: string, values?: ValidationMessageValues) => string | undefined,
): string {
  const parsed = parseValidationMessage(message);

  if (!parsed) {
    return message;
  }

  return lookup(parsed.key, parsed.values) ?? message;
}
